import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectClient, fetchClientByClientIdRequest } from '@client/model';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { useAllAllocations } from '@/features/guided-financial-planning/stores';
import type { PlanComparisonResult } from './use-plan-comparison-scenarios';

/**
 * Build a snapshot from the current plan comparison result.
 * This captures all the metrics needed to display the plan in read-only mode.
 */
function buildPlanComparisonSnapshot(planComparison: PlanComparisonResult): PlanComparisonSnapshot {
  return {
    projectedLtcCost: planComparison.projectedLtcCost,
    withoutInsurance: { ...planComparison.withoutInsurance },
    withInsurance: { ...planComparison.withInsurance },
    selectedPolicy: planComparison.selectedPolicy ? { ...planComparison.selectedPolicy } : null,
    fundingAllocations: planComparison.fundingAllocations.map(a => ({ ...a })),
    gapReduction: planComparison.gapReduction,
    netBenefit: planComparison.netBenefit,
    snapshotTimestamp: new Date().toISOString(),
  };
}

/**
 * Hook to finalize a guided planning session.
 *
 * This hook handles the complete finalization flow:
 * 1. Snapshots the plan comparison metrics for read-only mode
 * 2. Persists all selected quotes to the client's funding sources in the database
 * 3. Applies funding deductions to self-funding sources
 * 4. Marks the session as completed
 * 5. Refreshes the client data in Redux
 *
 * The policy is only persisted when the session is finalized, not when the quote is selected.
 */
export function useFinalizeSession() {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { session } = useSession();
  const { updateStepData, completeSession: markSessionComplete } = useSessionMutations();
  const allAllocations = useAllAllocations();
  const [isFinalizing, setIsFinalizing] = useState(false);

  /**
   * Persist all selected quotes to the client's funding sources.
   * Uses a direct API call (not Redux dispatch) to ensure the save completes
   * before subsequent steps run, avoiding race conditions with the client refresh.
   */
  const persistSelectedQuotes = useCallback(async (): Promise<void> => {
    if (!client?.clientId) return;

    const selectedQuotes = (session?.stepData?.selectedQuotes ?? {}) as Record<
      string,
      SelectedQuoteData
    >;
    const partnerClientId = client.mutableClientPartner?.clientId;

    for (const [, quoteData] of Object.entries(selectedQuotes)) {
      const { ccrcCouplePolicies, policyFundingSource } = quoteData;

      // A CCRC couple selection persists one membership per partner, the
      // primary's on the primary client, the partner's on the partner client.
      if (ccrcCouplePolicies && partnerClientId) {
        const { pendingFundingSourceId: primaryId, ...primaryToSave } = ccrcCouplePolicies.primary;
        await guidedPlanningApi.persistPolicyFundingSource({
          clientId: client.clientId,
          fundingSourceId: primaryId,
          fundingSourceData: primaryToSave,
        });

        const { pendingFundingSourceId: partnerId, ...partnerToSave } = ccrcCouplePolicies.partner;
        await guidedPlanningApi.persistPolicyFundingSource({
          clientId: partnerClientId,
          fundingSourceId: partnerId,
          fundingSourceData: partnerToSave,
        });
        continue;
      }

      const { pendingFundingSourceId, ...policyToSave } = policyFundingSource;
      await guidedPlanningApi.persistPolicyFundingSource({
        clientId: client.clientId,
        fundingSourceId: pendingFundingSourceId,
        fundingSourceData: policyToSave,
      });
    }
  }, [client?.clientId, client?.mutableClientPartner?.clientId, session?.stepData?.selectedQuotes]);

  /**
   * Apply funding deductions to self-funding sources based on user allocations.
   *
   * Deducts the full allocated policy amount from each selected account.
   */
  const applyFundingDeductions = useCallback(async (): Promise<void> => {
    if (!client?.clientId) return;

    const selfFundingSources = client.multipleFundingSources?.selfFunding ?? {};
    const selectedQuotes = session?.stepData?.selectedQuotes ?? {};
    const selectedQuoteId = Object.keys(selectedQuotes)[0];
    if (!selectedQuoteId) return;

    const selectedQuoteData = selectedQuotes[selectedQuoteId];
    if (!selectedQuoteData) return;

    const allocations = allAllocations[selectedQuoteId] ?? {};
    const updates: Array<{ fundingSourceId: string; existingAssetsValue: number }> = [];

    for (const [sourceId, allocatedAmount] of Object.entries(allocations)) {
      if (!allocatedAmount || allocatedAmount <= 0) continue;

      const source = selfFundingSources[sourceId];
      if (!source) continue;

      const originalValue = source.existingAssetsValue ?? 0;
      const newValue = Math.max(0, originalValue - allocatedAmount);

      updates.push({ fundingSourceId: sourceId, existingAssetsValue: newValue });
    }

    if (updates.length === 0) return;

    await guidedPlanningApi.batchUpdateSelfFundingAssets({
      clientId: client.clientId,
      updates,
    });
  }, [
    client?.clientId,
    client?.multipleFundingSources?.selfFunding,
    session?.stepData?.selectedQuotes,
    allAllocations,
  ]);

  /**
   * Finalize the session: snapshot metrics, persist policies, apply deductions, and mark session as completed.
   * @param planComparison - The current plan comparison result to snapshot
   */
  const finalizeSession = useCallback(
    async (planComparison: PlanComparisonResult) => {
      if (session?.status === 'completed') {
        return undefined;
      }

      setIsFinalizing(true);

      try {
        // 1. Snapshot the plan comparison metrics and funding sources for read-only mode
        const snapshot = buildPlanComparisonSnapshot(planComparison);
        const multipleFundingSourcesSnapshot = client?.multipleFundingSources
          ? (JSON.parse(JSON.stringify(client.multipleFundingSources)) as MultipleFundingSources)
          : undefined;
        await updateStepData({ planComparisonSnapshot: snapshot, multipleFundingSourcesSnapshot });

        // 2. Persist all selected quotes to the database (awaited to avoid race conditions)
        await persistSelectedQuotes();

        // 3. Apply funding deductions to self-funding sources (awaited)
        await applyFundingDeductions();

        // 4. Mark the session as completed
        await markSessionComplete();

        // 5. Refresh client data in Redux so the funding table and PDF reflect deductions
        if (client?.clientId) {
          dispatch(fetchClientByClientIdRequest(client.clientId));
        }
      } finally {
        setIsFinalizing(false);
      }
    },
    [
      session?.status,
      client?.multipleFundingSources,
      client?.clientId,
      updateStepData,
      persistSelectedQuotes,
      applyFundingDeductions,
      markSessionComplete,
      dispatch,
    ],
  );

  return {
    finalizeSession,
    isFinalizing,
  };
}
